import {
  Datagrid,
  Link,
  ListContextProvider,
  NumberField,
  TextField,
  useGetOne,
  useList,
  useRecordContext,
} from "react-admin";
import { DateField } from "../../fields/DateField";
import { type ReactNode, useState } from "react";
import { BackendUserRecord } from "../../providers/backendUserProvider";
import { Box, Card, CardContent, IconButton, Tooltip, Typography } from "@mui/material";
import DeleteIcon from "@mui/icons-material/Delete";
import { User } from "../../generated/backendClient";
import { ReferralCodeUsageDto } from "../../providers/referralCodeUsagesProvider";
import { grants } from "../../backoffice.access_control";
import { ConfirmDialog } from "../../dialogs/ConfirmDialog";

// infer type information from the generated backendClient (all accounts have the same structure, so we just pick the first one)
export type AccountInfo = User["accounts"][0]["balance"];

export const AccountBalance = ({
  title,
  accountInfo,
  children,
  showAmount = true,
  onRevokeContingent,
}: {
  title: string;
  accountInfo?: AccountInfo;
  children?: ReactNode;
  showAmount?: boolean;
  onRevokeContingent?: (contingentId: string) => Promise<void>;
}) => {
  return (
    <Card sx={{ marginTop: "1em" }}>
      <CardContent>
        <span style={{ fontWeight: 900 }}>{title}</span>
        <br />
        {children ? (
          children
        ) : accountInfo ? (
          <>
            {showAmount && (
              <NumberField
                source="amount"
                record={{ amount: parseFloat(accountInfo.balance.amount) }}
                options={{ style: "currency", currency: accountInfo.balance.currency }}
                style={{ fontWeight: 900, fontSize: "1.5rem" }}
              />
            )}
            <br />
            <TransactionList transactions={accountInfo.transactions} onRevokeContingent={onRevokeContingent} />
          </>
        ) : (
          <>
            <span>Keine Transaktionen</span>
          </>
        )}
      </CardContent>
    </Card>
  );
};

const moneyForNumberField = (money: AccountInfo["balance"]): { amount: number; currency: string } => {
  return { amount: parseFloat(money.amount), currency: money.currency };
};

const TransactionList = ({
  transactions,
  onRevokeContingent,
}: {
  transactions: AccountInfo["transactions"];
  onRevokeContingent?: (contingentId: string) => Promise<void>;
}) => {
  const listContext = useList({
    data: transactions.map((t) => {
      return {
        ...t,
        amount: moneyForNumberField(t.amount),
        description: t.description,
      };
    }),
  });
  const showRevokeColumn = onRevokeContingent && grants.has("giftCredits:add");
  return (
    <ListContextProvider value={listContext}>
      <Datagrid
        bulkActionButtons={false}
        isRowSelectable={() => false}
        empty={<Typography>Keine Transaktionen gefunden.</Typography>}
      >
        <DateField source="created_at" label="Datum" />
        <NumberField
          source="amount.amount"
          label="Betrag"
          options={{ style: "currency", currency: "EUR" }}
          style={{ whiteSpace: "nowrap" }}
        />
        <TransactionDescriptionField />
        {showRevokeColumn && <RevokeContingentButton onRevoke={onRevokeContingent} />}
      </Datagrid>
    </ListContextProvider>
  );
};

function RevokeContingentButton({ onRevoke }: { onRevoke: (contingentId: string) => Promise<void> }) {
  const record = useRecordContext<AccountInfo["transactions"][0]>();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);

  const payload = record?.payload;
  if (!payload?.is_revocable || !payload?.contingent_id) {
    return null;
  }
  if (typeof payload.contingent_id !== "string") {
    return null;
  }
  const contingentId = String(payload.contingent_id);
  return (
    <>
      <Tooltip title="Guthaben zurücknehmen">
        <IconButton
          size="small"
          color="error"
          onClick={(e) => {
            e.stopPropagation();
            setIsConfirmOpen(true);
          }}
        >
          <DeleteIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <ConfirmDialog
        open={isConfirmOpen}
        onClose={() => setIsConfirmOpen(false)}
        title="Guthaben zurücknehmen?"
        message="Soll dieses Guthaben wirklich zurückgenommen werden?"
        confirmButtonLabel="Ja, zurücknehmen"
        onConfirm={async () => {
          await onRevoke(contingentId);
          setIsConfirmOpen(false);
        }}
      />
    </>
  );
}

function TransactionDescriptionField() {
  const { description } = useRecordContext<AccountInfo["transactions"][0]>();
  const referralCodeUsageId = (description ?? "").match(referralCodeUsageRegex)?.[1];
  const { data: referralCodeUsage } = useGetOne<ReferralCodeUsageDto>(
    "referralCodeUsages",
    {
      id: referralCodeUsageId!,
    },
    { enabled: !!referralCodeUsageId },
  );

  if (!description) return null;
  if (referralCodeUsageId && !referralCodeUsage) return null;

  if (referralCodeUsage) {
    if (!referralCodeUsageId) {
      return null;
    }
    const descriptionChunks = description.split(referralCodeUsageId);
    return (
      <Box component="span">
        <Typography variant="body2" component="span">
          {descriptionChunks[0]}
        </Typography>
        by{" "}
        <Link to={`/students/${referralCodeUsage.appliedBy.studentId}`} underline="none">
          {`${referralCodeUsage.appliedBy.firstName} ${referralCodeUsage.appliedBy.lastName}`}
        </Link>
      </Box>
    );
  }

  return <TextField source="description" label="Beschreibung" />;
}
const referralCodeUsageRegex =
  /Reward for ReferralCodeUsage ([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}) claimed$/;

export type DrivingSchoolAccounts = BackendUserRecord["payload"]["accounts"][0];
